import { useRef, useState } from 'react'
import { useAtomValue } from 'jotai'

import { searchSubmissionAtom } from '../state'
import {
    fetchActiveReservationByToken,
    useChangeReservationGarage,
    useChangeReservationTime,
    useCreateReservation,
} from './api/use-reservation-checkout'
import {
    GENERIC_CHECKOUT_ERROR_MESSAGE,
    prepareCheckoutReservation,
} from './checkout-reservation-utils'
import { getStoredReservationToken } from '@/session-storage'

export function useCheckoutReservationHandler({
    garageId,
    onSuccess,
}: {
    garageId: number | string | undefined,
    onSuccess: () => void | Promise<void>,
}) {
    const searchSubmission = useAtomValue(searchSubmissionAtom)

    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const latestErrorMessageRef = useRef<string | null>(null)

    const handleMutationError = (message: string) => {
        latestErrorMessageRef.current = message
        setErrorMessage(message)
    }

    const createReservation = useCreateReservation({
        setRequestInProgress: () => {},
        onErrorCallback: handleMutationError,
    })

    const changeReservationTime = useChangeReservationTime({
        setRequestInProgress: () => {},
        onErrorCallback: handleMutationError,
    })

    const changeReservationGarage = useChangeReservationGarage({
        setRequestInProgress: () => {},
        onErrorCallback: handleMutationError,
    })

    const handleReserveAndCheckout = async () => {
        latestErrorMessageRef.current = null
        setErrorMessage(null)
        setLoading(true)

        try {
            await prepareCheckoutReservation({
                garageId,
                searchSubmission,
                hasStoredReservationToken: !!getStoredReservationToken(),
                refetchActiveReservation: async () => {
                    const reservationToken = getStoredReservationToken()

                    if (!reservationToken) {
                        throw new Error('Missing reservation token.')
                    }

                    return {
                        status: 'success',
                        data: await fetchActiveReservationByToken(reservationToken),
                    }
                },
                createReservation: createReservation.mutateAsync,
                changeReservationGarage: changeReservationGarage.mutateAsync,
                changeReservationTime: changeReservationTime.mutateAsync,
                onSuccess,
                onError: message => {
                    latestErrorMessageRef.current = message
                    setErrorMessage(message)
                },
            })
        } catch (error) {
            if (!latestErrorMessageRef.current) {
                setErrorMessage(
                    error instanceof Error && error.message
                        ? error.message
                        : GENERIC_CHECKOUT_ERROR_MESSAGE,
                )
            }
        } finally {
            setLoading(false)
        }
    }

    return {
        errorMessage,
        loading,
        handleReserveAndCheckout,
    }
}
